// useRepository.ts
import * as React from 'react';
import { useAuth } from '@app/Contexts/AuthProvider';
import type { CoreRepository } from '@api/Repositories/CoreRepository';

/**
 * Constructor type for any repository that extends CoreRepository.
 *
 * @template TRepository The concrete repository type extending CoreRepository.
 *
 * @example
 * class IndRepository extends CoreRepository {
 *   // ...
 * }
 *
 * const Ctor: RepositoryConstructor<IndRepository> = IndRepository;
 * const repo = new Ctor(); // repo is an IndRepository instance
 */
type RepositoryConstructor<TRepository extends CoreRepository> = new () => TRepository;

/**
 * React hook that returns an authenticated repository instance.
 *
 * The repository:
 * - Is constructed from the given repository class/constructor.
 * - Has the current access token from the Auth context applied via `setAccessToken`.
 * - Is memoised so a new instance is only created when the access token
 *   (or constructor) changes.
 *
 * @template TRepository The concrete repository type extending CoreRepository.
 *
 * @param {RepositoryConstructor<TRepository>} Constructor
 * The repository class/constructor to instantiate. Must extend CoreRepository.
 *
 * @returns {TRepository}
 * A repository instance with the current access token already applied.
 *
 * @example
 * ```ts
 * import { useRepository } from '@app/hooks/useRepository';
 * import { IndRepository } from '@api/Repositories/Ind/IndRepository';
 *
 * const MyComponent = () => {
 *   const individualRepository = useRepository(IndRepository);
 *
 *   React.useEffect(() => {
 *     individualRepository
 *       .getInds({ limit: 10 })
 *       .then(result => {
 *         // handle result
 *       });
 *   }, [individualRepository]);
 *
 *   return null;
 * };
 * ```
 */
export const useRepository = <TRepository extends CoreRepository>(
  Constructor: RepositoryConstructor<TRepository>
): TRepository => {
  // Get the current auth/session object from context.
  const session = useAuth();

  // Extract the current access token from the session.
  const token = session.getAccessToken();

  // Memoise the repository instance so we only recreate it when
  // the token (or the constructor) changes.
  const repo = React.useMemo(() => {
    const instance = new Constructor();
    instance.setAccessToken(token);
    return instance;
  }, [Constructor, token]);

  return repo;
};
